import { FC, useEffect, useMemo, useState } from 'react';
import styles from './styles.module.scss';
import { enumTranslate, ETransactionStatusTranslate } from '@locales/index';
import { Icon } from '@components/icons';
import Status from '@components/common/status';
import {
    copyStringToClipboard,
    formatDate,
    getFirstLettersBySpace,
    getFundsOperation,
    getScheduleDescription,
    getScheduleStatusVariant,
    getScheduleTypeByMethodAndAction,
    getStartDate,
    getTransactionAction,
    getTransactionStatus,
    prettifyAmount
} from '@utils/index';
import {
    EScheduleStatus,
    ETransactionAction,
    ETransactionMethod,
    ETransactionStatus,
    TTransaction
} from '@xeppt/xeppt-sdk/types';
import { IModalComponentProps } from '@context/modal_context';
import { TSchedule } from '@xeppt/xeppt-sdk/types/schedule';
import {
    getBillingStatusList,
    getFrequencyList,
    getLastPaymentDate,
    getNextPaymentDate
} from '@utils/billing';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames/bind';
import Dropdown from '@components/common/dropdown';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { apiAccountService, apiScheduleService, apiTransactionsService } from '@api';
import { useLocales } from '@hooks/helpers/useLocales';
import { useApiQuery } from '@hooks/api/useApiQuery';
import Typography from '@components/common/typography';
import { currencies } from '@const/currency';
import { ETransactionType, TTransactionLite } from '@xeppt/xeppt-sdk/types/transaction';
import { modalIds } from '@const/modals';
import useModalContext from '@hooks/context/useModalContext';
import InfoBlock from '@components/common/info_block';
import Button from '@components/common/button';
import { EFundsOperation } from '@enum';

const cx = classNames.bind(styles);

const ViewScheduleModal: FC<
    IModalComponentProps<{
        schedule: TSchedule;
        refetchSchedules?: () => void;
        onEdit: (schedule: TSchedule) => void;
    }>
> = ({ onClose, modalData }) => {
    const { t: frequencyLocale, i18n } = useTranslation('sections', {
        keyPrefix: 'send_and_request.schedule_payments.payment_details'
    });
    const { t: transactionLocale } = useTranslation('components', {
        keyPrefix: 'transaction_tables.transaction_item'
    });
    const { requestErrorLocale, requestSuccessLocale, submitLocale } = useLocales();
    const [isTransactionsOpen, setIsTransactionsOpen] = useState(false);
    const [chosenTransaction, setChosenTransaction] = useState('');
    const { isOpen, onOpen, changeModalData } = useModalContext();

    const { data: transactions } = useApiQuery({
        method: () =>
            apiTransactionsService.findTransactions({ schedule_id: modalData?.schedule?.id }),
        deps: [modalData?.schedule?.id],
        condition: !!modalData?.schedule?.id
    });

    const { handleRequest: fetchTransaction } = useApiQuery({
        method: () => apiTransactionsService.getTransactionById(chosenTransaction || ''),
        isInitialRequest: false,
        condition: !!chosenTransaction,
        onSuccess: (data) => {
            handleViewTransaction(data);
        },
        onError: () => {
            setChosenTransaction('');
        }
    });

    const { handleRequest: handleRetrySchedule, isLoading: isRetryLoading } = useApiMutation({
        method: (id: string) => {
            return apiScheduleService.retrySchedule(id);
        },
        onSuccess: () => {
            requestSuccessLocale('retry_schedule');
            fetchTransaction();
            onClose();
        },
        onError: requestErrorLocale
    });

    useEffect(() => {
        if (chosenTransaction) {
            fetchTransaction();
        }
    }, [chosenTransaction]);

    useEffect(() => {
        if (!isOpen) {
            setChosenTransaction('');
        }
    }, [isOpen]);

    const handleViewTransaction = (transaction: TTransaction) => {
        changeModalData({ transaction });
        onOpen({
            modalId: modalIds.VIEW_TRANSACTION
        });
    };

    if (!modalData?.schedule) return null;

    const { schedule, refetchSchedules, onEdit } = modalData;

    const isRequest = useMemo(() => {
        return schedule.action === ETransactionAction.REQUEST;
    }, [schedule]);

    const getRecipientName = useMemo(() => {
        return schedule.info.split('(')?.[0] || '';
    }, [schedule]);

    const getEmail = useMemo(() => {
        return schedule.info.split('(')?.[1]?.replace(')', '') || '';
    }, [schedule]);

    const getAvatar = useMemo(() => {
        if (
            schedule.method === ETransactionMethod.WALLET ||
            schedule.method === ETransactionMethod.E_TRANSFER
        ) {
            return getFirstLettersBySpace(getRecipientName);
        } else if (ETransactionMethod.EFT === schedule.method) {
            return <Icon name="bank" width={24} height={24} />;
        } else if (ETransactionMethod.EPS === schedule.method) {
            return <Icon name="credit_card" width={24} height={24} />;
        } else if (ETransactionMethod.PAY_BILL === schedule.method) {
            return <Icon name="pay_bill" width={24} height={24} />;
        }
        return '';
    }, [schedule, getRecipientName]);

    const getNextPaymentText = useMemo(() => {
        if (schedule.status === EScheduleStatus.COMPLETE) {
            return 'Closed';
        } else if (
            schedule.status === EScheduleStatus.ACTIVE ||
            schedule.status === EScheduleStatus.ERROR
        ) {
            return 'Next payment';
        } else if (schedule.status === EScheduleStatus.INACTIVE) {
            return 'Paused';
        }
    }, []);

    const { handleRequest: handleCancelPayment } = useApiMutation({
        method: (id: string) => {
            return apiScheduleService.deleteSchedule(id);
        },
        onSuccess: () => {
            requestSuccessLocale('cancel_schedule');
            refetchSchedules && refetchSchedules();
            onClose();
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleDeactivatePayment } = useApiMutation({
        method: (id: string) => {
            return apiScheduleService.deactivateSchedule(id);
        },
        onSuccess: () => {
            requestSuccessLocale('pause_schedule');
            refetchSchedules && refetchSchedules();
            onClose();
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleActivatePayment } = useApiMutation({
        method: (id: string) => {
            return apiScheduleService.activateSchedule(id);
        },
        onSuccess: () => {
            requestSuccessLocale('activate_schedule');
            refetchSchedules && refetchSchedules();
            onClose();
        },
        onError: requestErrorLocale
    });

    const handlePauseResumePayment = (id: string, status: EScheduleStatus) => {
        if (status === EScheduleStatus.ACTIVE) {
            handleDeactivatePayment(id);
        } else {
            handleActivatePayment(id);
        }
    };

    const getTitle = useMemo(() => {
        const type = getScheduleTypeByMethodAndAction(schedule.method, schedule.action);
        const operation = `${schedule.method}_${type}_${schedule.action}`;
        switch (operation) {
            case EFundsOperation.WALLET_CREDIT_TRANSFER:
                return 'Scheduled send money via XEPPT';
            case EFundsOperation.WALLET_DEBIT_REQUEST:
                return 'Scheduled request money via XEPPT';
            case EFundsOperation.EFT_CREDIT_WITHDRAWAL:
                return 'Scheduled send money to bank account';
            case EFundsOperation.EFT_DEBIT_DEPOSIT:
                return 'Scheduled load balance';
            case EFundsOperation.PAY_BILL_CREDIT_PAYMENT:
                return 'Scheduled pay bill';
        }
    }, [schedule]);

    const transactionAmount = (transaction: TTransactionLite) => {
        if (transaction.type === ETransactionType.CREDIT) {
            return transaction.amount + transaction.fee;
        }
        return transaction.amount;
    };

    const amountStyles = (transaction: TTransactionLite) =>
        cx([
            styles.amount,
            {
                plus: transaction.type !== ETransactionType.CREDIT,
                isCancel:
                    transaction.status === ETransactionStatus.CANCELED ||
                    transaction.status === ETransactionStatus.DECLINED
            }
        ]);

    const calculateTotalPaid = useMemo(() => {
        return (
            transactions?.data?.reduce((acc, transaction) => {
                if (transaction.status === ETransactionStatus.APPROVED)
                    return acc + transactionAmount(transaction);
                return acc;
            }, 0) || 0
        );
    }, [transactions]);

    const { data: pointsFeeData } = useApiQuery({
        method: () => {
            return apiAccountService.calculateLoyaltyFee({
                fee_included: false,
                amount: schedule.amount,
                method: schedule.method,
                action: schedule.action,
                type: getScheduleTypeByMethodAndAction(schedule.method, schedule.action)
            });
        },
        isInitialRequest: false,
        deps: [schedule],
        condition: !!schedule
    });

    const calculateRemainingAmount = useMemo(() => {
        const fee = pointsFeeData?.fee || 0;
        return (schedule.amount + fee) * schedule.remaining_payments || 0;
    }, [pointsFeeData, schedule]);

    return (
        <div className={styles.wrapper}>
            {schedule.status === EScheduleStatus.ERROR && (
                <InfoBlock variant="warning">
                    <div className={styles.error_info}>
                        <Typography variant="body4" weight="semibold">
                            Last payment attempt failed
                        </Typography>
                        <Typography variant="body4">
                            Check your balance and recipient details to ensure they’re correct. Once
                            the issue is resolved, retry the payment.
                        </Typography>
                        <Button
                            size="small"
                            variant="primary"
                            isLoading={isRetryLoading}
                            onClick={() => handleRetrySchedule(schedule.id)}>
                            Retry payment
                        </Button>
                    </div>
                </InfoBlock>
            )}
            <div className={styles.content}>
                <div className={styles.user}>
                    <Dropdown
                        className={`${styles.actions} modal_dropdown_index`}
                        items={[
                            schedule.status === EScheduleStatus.ERROR && {
                                label: submitLocale('retry'),
                                icon: 'refresh',
                                onClick: () => handleRetrySchedule(schedule.id)
                            },
                            (schedule.status === EScheduleStatus.ACTIVE ||
                                schedule.status === EScheduleStatus.INACTIVE) && {
                                label:
                                    schedule.status === EScheduleStatus.ACTIVE
                                        ? submitLocale('pause')
                                        : submitLocale('continue'),
                                icon: schedule.status === EScheduleStatus.ACTIVE ? 'pause' : 'play',
                                onClick: () =>
                                    handlePauseResumePayment(schedule.id, schedule.status)
                            },
                            schedule.status !== EScheduleStatus.COMPLETE && {
                                label: submitLocale('edit'),
                                icon: 'edit',
                                onClick: () => onEdit(schedule)
                            },
                            {
                                label:
                                    schedule.status === EScheduleStatus.ERROR
                                        ? submitLocale('delete')
                                        : submitLocale('cancel'),
                                isRed: true,
                                icon:
                                    schedule.status === EScheduleStatus.ERROR
                                        ? 'trash'
                                        : 'close_outlined',
                                onClick: () => handleCancelPayment(schedule.id)
                            }
                        ]}
                    />
                    <div className={styles.user_avatar}>{getAvatar}</div>
                    <p className={styles.user_name}>{getRecipientName}</p>
                    <p className={styles.user_email}>{getEmail}</p>
                    <p className={styles.method}>
                        {schedule?.method &&
                            enumTranslate[schedule.method][i18n.language as 'en' | 'fr']}
                    </p>
                    <div className={styles.divider}>
                        <Typography variant="body4">
                            <Icon
                                name={
                                    getScheduleTypeByMethodAndAction(
                                        schedule.method,
                                        schedule.action
                                    ) === ETransactionType.CREDIT
                                        ? 'arrow_up'
                                        : 'arrow_down'
                                }
                            />
                            {getTitle}
                        </Typography>
                    </div>
                    <div className={styles.payment_id}>
                        <p>Schedule payment ID:</p>
                        <div className={styles.id}>
                            <p>{schedule.id}</p>
                            <button onClick={() => copyStringToClipboard(schedule.id)}>
                                <Icon name="copy" width={18} height={18} />
                            </button>
                        </div>
                    </div>
                    <Status variant={getScheduleStatusVariant(schedule?.status)}>
                        {schedule?.status &&
                            getBillingStatusList(frequencyLocale).find(
                                (item) => item.value === schedule?.status
                            )?.label}
                    </Status>
                    <div className={styles.details}>
                        <div className={styles.row}>
                            <div>
                                <b style={{ fontWeight: 600 }}>${schedule.amount.toFixed(2)} </b>{' '}
                                <span className={styles.light_gray}>
                                    /{' '}
                                    {getFrequencyList(frequencyLocale)
                                        .find((item) => item.value === schedule.frequency)
                                        ?.label?.toLowerCase() || ''}
                                </span>
                            </div>
                            <div>
                                Occurrences:{' '}
                                {[EScheduleStatus.ERROR, EScheduleStatus.COMPLETE].includes(
                                    schedule.status
                                )
                                    ? '0'
                                    : schedule.remaining_payments > 0
                                      ? schedule.total_payments + schedule.remaining_payments
                                      : 'Indefinite'}
                            </div>
                        </div>
                        <div className={styles.progress}>
                            <div className={styles.progress_title}>
                                {schedule.remaining_payments > 0
                                    ? `Payments remaining: ${schedule.remaining_payments}`
                                    : `${isRequest ? 'Requested' : 'Paid'}: $${calculateTotalPaid.toFixed(2)}`}{' '}
                                {schedule.remaining_payments <= 0 && (
                                    <span>({schedule.total_payments} payments)</span>
                                )}
                            </div>
                            {schedule.remaining_payments > 0 && (
                                <div className={styles.progress_bg}>
                                    <div
                                        style={{
                                            width: `${(100 / (schedule.total_payments + schedule.remaining_payments)) * schedule.total_payments}%`
                                        }}
                                    />
                                </div>
                            )}
                            {schedule.remaining_payments > 0 && (
                                <div className={styles.row}>
                                    <div>Paid: ${calculateTotalPaid.toFixed(2)}</div>
                                    <div>Remaining: ${calculateRemainingAmount.toFixed(2)}</div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className={styles.dates}>
                    <div className={styles.row}>
                        <div className={styles.date}>
                            <Icon name="calendar" />
                            <div className={styles.text_wrapper}>
                                <p>Start date</p>
                                <span>
                                    {formatDate(
                                        getStartDate({
                                            nextPaymentAt: schedule.next_payment_at,
                                            lastPaymentAt: schedule.last_payment_at,
                                            frequency: schedule.frequency,
                                            donePayments: schedule.total_payments
                                        }),
                                        'MM/DD/YYYY'
                                    )}
                                </span>
                            </div>
                        </div>
                        <div className={styles.dashed_divider} />
                        <div className={styles.date}>
                            <Icon name="calendar_check" />
                            <div className={styles.text_wrapper}>
                                <p>Due date</p>
                                <span>
                                    {schedule?.remaining_payments >= 0
                                        ? formatDate(
                                              getLastPaymentDate({
                                                  nextPaymentAt: schedule.next_payment_at,
                                                  lastPaymentAt: schedule.last_payment_at,
                                                  frequency: schedule.frequency,
                                                  number_of_payments: schedule?.remaining_payments,
                                                  donePayments: schedule.total_payments
                                              }),
                                              'MM/DD/YYYY'
                                          )
                                        : 'Until canceled'}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className={styles.divider} />
                    <div className={styles.row}>
                        <div className={styles.date}>
                            <Icon name="reload" />
                            <div className={styles.text_wrapper}>
                                <p>Payment frequency </p>
                                <span>
                                    {getScheduleDescription(
                                        getStartDate({
                                            nextPaymentAt: schedule.next_payment_at,
                                            lastPaymentAt: schedule.last_payment_at,
                                            frequency: schedule.frequency,
                                            donePayments: schedule.total_payments
                                        }),
                                        schedule.frequency
                                    )}
                                </span>
                            </div>
                        </div>
                        <div className={styles.date}>
                            <div
                                className={cx([
                                    styles.dot,
                                    getScheduleStatusVariant(schedule?.status)
                                ])}
                            />
                            <div className={styles.text_wrapper}>
                                <p>{getNextPaymentText}</p>
                                <span>
                                    {formatDate(
                                        schedule?.status !== EScheduleStatus.ACTIVE
                                            ? schedule.last_payment_at
                                            : getNextPaymentDate({
                                                  frequency: schedule.frequency,
                                                  nextPaymentAt: schedule.next_payment_at,
                                                  lastPaymentAt: schedule.last_payment_at
                                              }),
                                        'MM/DD/YYYY'
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className={styles.divider} />
                    <div className={styles.column}>
                        <div
                            className={cx([
                                styles.transactions_button,
                                { isOpen: isTransactionsOpen }
                            ])}
                            onClick={() => setIsTransactionsOpen((state) => !state)}>
                            <Icon name="history" />
                            <Typography variant="body4">Transaction history</Typography>
                            <Icon name="nav_down" />
                        </div>
                        <div className={cx([styles.dropdown, { isOpen: isTransactionsOpen }])}>
                            {transactions?.data?.map((item) => {
                                return (
                                    <div
                                        className={styles.item}
                                        key={item.id}
                                        onClick={() => {
                                            setChosenTransaction(item.id);
                                        }}>
                                        <Typography variant="body3">
                                            {formatDate(item.created_at, 'DD MMM YYYY')}
                                        </Typography>
                                        <div className={styles.right}>
                                            {item.status !== ETransactionStatus.APPROVED && (
                                                <Status
                                                    variant={getTransactionStatus(item?.status)}
                                                    className={styles.status}>
                                                    {item?.status &&
                                                        //@ts-ignore
                                                        ETransactionStatusTranslate[item.status][
                                                            i18n.language as 'en' | 'fr'
                                                        ]}
                                                </Status>
                                            )}
                                            <Typography
                                                variant="body1"
                                                weight="bold"
                                                className={amountStyles(item)}>
                                                {item.type === ETransactionType.CREDIT && '-'}
                                                {currencies[item.currency as 'CAD']?.symbol}
                                                {prettifyAmount(transactionAmount(item).toFixed(2))}
                                            </Typography>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewScheduleModal;
